// Phase 6 — Track C T-C2. AuthContext.
//
// Holds the SPA's view of authenticated state and exposes `useAuth()`. Per
// plan §2.5 + Q-8 the provider blocks initial render until a single
// `apiClient.auth.me()` call resolves — a centred loader is rendered until
// the result lands. This eliminates the flash-of-unauthenticated-content
// that the §13.1 "calm and confident" rules explicitly forbid.
//
// Status state machine:
//
//   'loading'         — initial render; me() is in flight
//   'authenticated'   — me() returned 200 with a User
//   'unauthenticated' — me() returned 401, OR signOut() was called
//
// signIn(user)        — moves the cache to authenticated. The Login route
//                       calls this after a successful POST /auth/login so
//                       downstream queries (and RequireAuth) see the new
//                       state immediately, without an extra me() round-trip.
// signOut()           — moves the cache to unauthenticated. The UserMenu
//                       calls this after a successful POST /auth/logout,
//                       and the queryClient global-error handler calls it
//                       on AuthRequiredError to drop stale state before
//                       redirecting to /login.
//
// The provider is intentionally NOT a react-query hook itself — it owns
// the bootstrap me() call directly so it can block render. Routes that
// want to refetch /me reach for the queryClient via `invalidateQueries`
// against the canonical key `AUTH_ME_QUERY_KEY`.

import * as React from 'react';
import { AuthRequiredError } from '@dailydeck/shared/api';
import type { User } from '@dailydeck/shared/types';
import { Skeleton } from '../components/ui/Skeleton.js';
import { apiClient } from './apiClient.js';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: User | null;
  /** Promotes the cache to authenticated. Called after a successful login. */
  readonly signIn: (user: User) => void;
  /** Drops the cache to unauthenticated. Does NOT call /auth/logout itself. */
  readonly signOut: () => void;
}

/** Canonical react-query key for the /auth/me bootstrap. */
export const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const;

const AuthContext = React.createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  readonly children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [status, setStatus] = React.useState<AuthStatus>('loading');
  const [user, setUser] = React.useState<User | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    apiClient.auth
      .me()
      .then((res) => {
        if (cancelled) return;
        setUser(res.user);
        setStatus('authenticated');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof AuthRequiredError) {
          setUser(null);
          setStatus('unauthenticated');
          return;
        }
        // Network or 5xx during bootstrap — treat as unauthenticated; the
        // login route will surface a network-unavailable error if relevant.
        setUser(null);
        setStatus('unauthenticated');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = React.useCallback((nextUser: User) => {
    setUser(nextUser);
    setStatus('authenticated');
  }, []);

  const signOut = React.useCallback(() => {
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({ status, user, signIn, signOut }),
    [status, user, signIn, signOut],
  );

  if (status === 'loading') {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-surface-base"
        role="status"
        aria-live="polite"
        aria-label="Loading"
      >
        <Skeleton className="h-10 w-40" />
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() must be used within an <AuthProvider>');
  }
  return ctx;
}
